import React from 'react';
import { Stack, Text } from '@atlaskit/primitives';
import { DynamicResultTable, FormattedDate, FormatCurrency } from '@css-xp/base';
import { getLatestPeriodEndDate } from '@css-xp/entitlements-v2';
import { isFutureDatedInactiveSubscription, isMultiInstanceOffering } from '@css-xp/ppc-utils';
import { CloudProductDetailView, EntitlementStatus, getBilledForColumnData } from '@css-xp/base/entitlement-utils';
import {
  EntitlementTags,
  errorStateProp,
  emptyStateProp,
  CloudSiteLink,
  NoData,
  TableHeading,
  emptyStatePropForAppsTable,
  Usage,
} from '@css-xp/ppc-common';
import {
  BillingStatus,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import { isEntitlementOnGracePeriod } from '@css-xp/ppc-utils/src/entitlement-utils';

import capitalize from 'lodash/capitalize';
import { useFetchLinkedEntitlementsPPCQuery } from '../../../hooks/use-fetch-linked-entitlements-ppc-query';
import './linked-entitlements-tab.scss';

export interface LinkedEntitlementsProps {
  id: string | undefined;
  transactionAccountId?: string | null;
  currentEntitlementType?: LinkedEntitlementTypes;
  destinationEntitlementType: Array<LinkedEntitlementTypes>;
  productType: LinkedProductTypes;
  findProducts?: boolean;
  findApps?: boolean;
  entitlementId: string;
  tableHeading?: string;
  baseProduct?: string;
  isDesignIteration1Enabled?: boolean;
  isFutureDatedEntitlementEnabled?: boolean;
  isMultiInstanceAppsEnabled?: boolean;
}

export const LinkedEntitlementsTab = ({
  id,
  transactionAccountId,
  currentEntitlementType,
  destinationEntitlementType,
  productType,
  findProducts,
  findApps,
  entitlementId,
  baseProduct = '',
  tableHeading = 'Apps',
  isDesignIteration1Enabled,
  isFutureDatedEntitlementEnabled = false,
  isMultiInstanceAppsEnabled = false,
}: LinkedEntitlementsProps) => {
  const { data, loading, error } = useFetchLinkedEntitlementsPPCQuery({
    entitlementId: entitlementId,
    containerId: id,
    currentEntitlementType: currentEntitlementType,
    destinationEntitlementType: destinationEntitlementType,
    productType: productType,
    findProducts: findProducts,
    findApps: findApps,
    transactionAccountId: transactionAccountId,
  });

  const linkedEntitlements: Array<LinkedDestinationEntitlement> = data?.entitlementDetails
    .linkedEntitlements as Array<LinkedDestinationEntitlement>;

  const totalCount = linkedEntitlements?.length || 0;
  const getRows = () => {
    return (linkedEntitlements ?? [])
      .filter((entitlement) => entitlement.entitlementId !== entitlementId)
      .filter((entitlement) => (baseProduct ? entitlement.baseProduct === baseProduct : true))
      .map((item, index) => {
        const cloudSiteHostname = item?.cloudSiteHostname;

        const activeUsers = item?.activeUsers ?? 0;
        //@ts-ignore
        const isGracePeriodEntitlement = isEntitlementOnGracePeriod(item);
        const productDetailView = (
          <div>
            <CloudProductDetailView
              logoId={item?.logoId as string}
              productId={item?.productId as string}
              entitlementNumber={item.entitlementNumber ?? ''}
              provisioningStatus={item?.provisioningStatus}
              edition={item?.edition}
              productName={item?.productName}
              cloudSiteId={item?.cloudSiteId}
              trial={item?.trial ?? false}
              offeringPricingType={item?.offering?.pricingType as string}
              isApp={item?.isApp as boolean}
              isSandbox={item?.isSandbox as boolean}
              productLink={item?.entitlementId ?? item?.entitlementNumber}
              {...(isDesignIteration1Enabled ? { version: 2 } : {})}
              isAddOn={!!item?.isAddOn}
              shouldHideEntitlementDetails={isDesignIteration1Enabled}
              shouldHideExtraInfo={isDesignIteration1Enabled}
            />
            {isDesignIteration1Enabled && <EntitlementTags isEntitlementOnGracePeriod={isGracePeriodEntitlement} />}
          </div>
        );

        const cloudSiteLink = (
          <CloudSiteLink siteName={cloudSiteHostname} txaId={transactionAccountId} cloudSiteId={item?.cloudSiteId} />
        );

        const usage = (
          <div>
            {isDesignIteration1Enabled ? (
              <>
                <Usage
                  activeUsers={activeUsers}
                  userTier={item?.userTier}
                  productId={item?.productId as string}
                  edition={item?.edition as string}
                  chargeElement={item?.chargeElement}
                />
              </>
            ) : (
              <>
                <div>{capitalize(getBilledForColumnData(item?.productId, item?.edition))}</div>
                <div>{activeUsers}</div>
              </>
            )}
          </div>
        );

        const createdDate = item.createdDate ? <FormattedDate date={item.createdDate} /> : <NoData />;

        const latestPeriodEndDate = item?.latestPeriodEndDate ? (
          getLatestPeriodEndDate(item?.billingStatus, item?.latestPeriodEndDate)
        ) : (
          <NoData />
        );

        const isFutureDatedInactiveEntitlement =
          isFutureDatedEntitlementEnabled &&
          isFutureDatedInactiveSubscription(!!item?.isFutureDatedSubscription, item?.billingStatus as BillingStatus);

        const entitlementStatus = item.billingStatus ? (
          <EntitlementStatus
            billingStatus={item.billingStatus}
            provisioningStatus={item?.provisioningStatus}
            isFutureDatedInactiveEntitlement={isFutureDatedInactiveEntitlement}
            hideInactiveStatus={isFutureDatedInactiveEntitlement}
            trial={item?.trial ?? false}
          />
        ) : (
          <NoData />
        );

        const lastBilledDate = item?.lastPeriodStartDate ? (
          <FormattedDate date={item.lastPeriodStartDate} />
        ) : (
          <NoData />
        );

        const plan = (
          <Stack>
            {item.edition ? <Text>{capitalize(item.edition)}</Text> : <NoData />}
            <Text size="small" color="color.text.subtlest">
              {item.billingFrequency ? capitalize(item.billingFrequency) : <NoData />}
            </Text>
          </Stack>
        );

        const isMultiInstanceAppInstanceProduct =
          isMultiInstanceAppsEnabled && item?.productId && isMultiInstanceOffering(item?.offering, item.productId);

        const nextPriceEstimate =
          item?.nextBillEstimate && !isMultiInstanceAppInstanceProduct ? (
            <FormatCurrency
              showCurrencyCode
              showCommas
              currency={item?.nextBillEstimate?.currency}
              value={item?.nextBillEstimate?.total ?? 0}
            />
          ) : (
            <NoData />
          );

        const cellsContent = (() => {
          if (isDesignIteration1Enabled) {
            return [
              {
                content: productDetailView,
              },
              {
                content: entitlementStatus,
              },
              {
                content: plan,
              },
              {
                content: usage,
              },
              {
                content: createdDate,
              },
              {
                content: latestPeriodEndDate,
              },
              {
                content: nextPriceEstimate,
              },
            ];
          }
          return [
            {
              content: productDetailView,
            },
            {
              content: cloudSiteLink,
            },
            {
              content: usage,
            },
            {
              content: createdDate,
            },
            {
              content: entitlementStatus,
            },
            {
              content: lastBilledDate,
            },
            {
              content: latestPeriodEndDate,
            },
            {
              content: createdDate,
            },
          ];
        })();

        return {
          key: index.toString(),
          cells: cellsContent,
        };
      });
  };

  const LINKED_ENTITLEMENT_HEADER = {
    NAME: {
      content: 'Name',
      shouldTruncate: true,
      width: 25,
    },
    STATUS: {
      content: 'Status',
      width: 10,
    },
    USAGE: {
      content: isDesignIteration1Enabled ? 'Quantity' : 'Usage',
      width: 10,
    },
    SITE_NAME: {
      content: 'Site name',
      width: 25,
    },
    ADDED: {
      content: 'Added',
      width: 15,
    },
    NEXT_BILL: {
      content: 'Next bill',
      width: 10,
    },
    LAST_BILLED: {
      content: 'Last billed',
      width: 10,
    },
    CREATED: {
      content: 'Created',
      width: 10,
    },
    PLAN: {
      content: 'Plan',
      width: 15,
    },
    NEXT_PRICE: {
      content: 'Next price estimate',
      width: 10,
    },
  };

  const getHead = () => {
    const headerContent = (() => {
      if (isDesignIteration1Enabled) {
        if (totalCount === 0) {
          return [];
        }
        return [
          LINKED_ENTITLEMENT_HEADER.NAME,
          LINKED_ENTITLEMENT_HEADER.STATUS,
          LINKED_ENTITLEMENT_HEADER.PLAN,
          LINKED_ENTITLEMENT_HEADER.USAGE,
          { ...LINKED_ENTITLEMENT_HEADER.ADDED, content: 'Date added' },
          { ...LINKED_ENTITLEMENT_HEADER.NEXT_BILL, content: 'Renew date' },
          LINKED_ENTITLEMENT_HEADER.NEXT_PRICE,
        ];
      }
      return [
        LINKED_ENTITLEMENT_HEADER.NAME,
        LINKED_ENTITLEMENT_HEADER.SITE_NAME,
        LINKED_ENTITLEMENT_HEADER.USAGE,
        LINKED_ENTITLEMENT_HEADER.ADDED,
        LINKED_ENTITLEMENT_HEADER.STATUS,
        LINKED_ENTITLEMENT_HEADER.LAST_BILLED,
        LINKED_ENTITLEMENT_HEADER.NEXT_BILL,
        LINKED_ENTITLEMENT_HEADER.CREATED,
      ];
    })();
    return {
      cells: headerContent,
    };
  };

  return (
    <div className="statuspage-tab" data-testid="statuspage-add-ons">
      <DynamicResultTable
        testId="linked-entitlements"
        beforeTable={!isDesignIteration1Enabled ? <TableHeading>{tableHeading}</TableHeading> : <></>}
        head={getHead()}
        rows={getRows()}
        emptyStateProps={isDesignIteration1Enabled ? emptyStatePropForAppsTable : emptyStateProp}
        isLoading={loading}
        errorStateProps={errorStateProp}
        error={error?.message}
        totalCount={totalCount}
      />
    </div>
  );
};
